import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import { Group, AssignmentTurnedIn, TrendingUp, MonetizationOn, LocalHospital, PictureAsPdf } from '@mui/icons-material';
import { getCollection } from '../services/firestoreService';
import HospitalCasuistryTab from '../components/HospitalCasuistryTab';
import SurgeryDashboardTab from '../components/SurgeryDashboardTab';
import HospitalEgresosStatsTab from '../components/HospitalEgresosStatsTab';
import AgendaDashboardPage from './AgendaDashboardPage';

const PERIOD_OPTIONS = [
  { value: '7', label: 'Últimos 7 días' },
  { value: '30', label: 'Últimos 30 días' },
  { value: '90', label: 'Últimos 90 días' },
  { value: 'custom', label: 'Personalizado' },
];

const PIE_COLORS = ['#f44336', '#ff9800', '#ffc107', '#4caf50', '#2196f3', '#9c27b0', '#795548', '#607d8b'];

const getTodayIso = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const shiftDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getCurrentMonthLabel = () => {
  const d = new Date();
  const months = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  return `${months[d.getMonth()]} ${d.getFullYear()}`;
};

const getDateFromValue = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  // Si viene como string YYYY-MM-DD
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  return new Date(value);
};

const getPeriodRange = (period) => {
  // period: YYYY-MM
  const [year, month] = (period || '').split('-').map(Number);
  if (!year || !month) return { start: null, end: null };
  const start = new Date(year, month - 1, 1, 0, 0, 0, 0);
  const end = new Date(year, month, 0, 23, 59, 59, 999);
  return { start, end };
};

const isInPeriod = (value, period) => {
  const d = getDateFromValue(value);
  const { start, end } = getPeriodRange(period);
  if (!d || !start || !end) return false;
  return d >= start && d <= end;
};

/**
 * Calcula el porcentaje de avance del mes (0-100) para un periodo YYYY-MM.
 * - Mes actual: (dia del mes / dias totales del mes) * 100
 * - Mes pasado: 100 (mes completo)
 * - Mes futuro: 0
 */
const getMonthProgress = (period) => {
  const [year, month] = (period || '').split('-').map(Number);
  if (!year || !month) return 100;
  const now = new Date();
  const isCurrentMonth = now.getFullYear() === year && now.getMonth() + 1 === month;
  if (!isCurrentMonth) {
    // Mes pasado → 100%, mes futuro → 0%
    const monthDate = new Date(year, month - 1, 1);
    return now > monthDate ? 100 : 0;
  }
  const totalDays = new Date(year, month, 0).getDate();
  const currentDay = now.getDate();
  return Math.min(100, (currentDay / totalDays) * 100);
};

const FunnelStage = ({ label, value, percentOfTop, color, subtitle }) => {
  const safePercent = Number.isFinite(percentOfTop) ? Math.max(0, Math.min(percentOfTop, 100)) : 0;
  const barPercent = value > 0 ? Math.max(safePercent, 5) : 0;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="subtitle2">{value}</Typography>
      </Box>
      <Box
        sx={{
          position: 'relative',
          height: 18,
          bgcolor: 'grey.200',
          borderRadius: 9,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${barPercent}%`,
            bgcolor: color,
            transition: 'width 0.3s ease',
          }}
        />
      </Box>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

const ClosedReasonsPie = ({ data, total }) => {
  let backgroundImage = 'conic-gradient(#e0e0e0 0deg 360deg)';
  let segments = [];

  if (total && data && data.length) {
    let currentAngle = 0;
    segments = data.map((item) => {
      const pct = item.pct || 0;
      const angle = pct * 3.6; // 100% = 360deg
      const startAngle = currentAngle;
      const endAngle = startAngle + angle;
      currentAngle = endAngle;
      return {
        ...item,
        startAngle,
        endAngle,
        midAngle: startAngle + angle / 2,
      };
    });

    const pieces = segments.map(
      (seg) => `${seg.color} ${seg.startAngle}deg ${seg.endAngle}deg`
    );
    backgroundImage = `conic-gradient(${pieces.join(', ')})`;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 200,
          height: 200,
          borderRadius: '50%',
          backgroundImage,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: '22%',
            borderRadius: '50%',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Total cerradas
          </Typography>
          <Typography variant="h6">{total}</Typography>
        </Box>

        {segments.map((seg) => {
          if (!seg.pct) return null;
          const radius = 70; // radio para las etiquetas dentro de la dona
          // Ajustar el ángulo: CSS conic-gradient comienza a las 12 (arriba) y va en sentido horario
          // Convertir: restar 90° para que 0° esté arriba en lugar de la derecha
          const adjustedAngle = seg.midAngle - 90;
          const rad = (adjustedAngle * Math.PI) / 180;
          const x = Math.cos(rad) * radius;
          const y = Math.sin(rad) * radius;

          return (
            <Box
              key={seg.reason}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
                pointerEvents: 'none',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#ffffff',
                  textShadow: '0 0 3px rgba(0,0,0,0.6)',
                }}
              >
                {`${seg.pct.toFixed(1)}%`}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {(!data || !data.length) && (
          <Typography variant="body2" color="text.secondary">
            No hay datos de motivos de cierre.
          </Typography>
        )}
        {data &&
          data.map((item) => (
            <Box
              key={item.reason}
              sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: item.color,
                  flexShrink: 0,
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    maxWidth: '100%',
                  }}
                >
                  {item.reason}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, flexShrink: 0 }}
                >
                  {`${item.pct.toFixed(1)}% (${item.count})`}
                </Typography>
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );
};

const GoalBar = ({ title, target, actual, currency = false }) => {
  const t = Number(target) || 0;
  const a = Number(actual) || 0;
  const pct = t > 0 ? (a / t) * 100 : 0;
  const max = Math.max(t, a, 1);
  const targetWidth = (t / max) * 100;
  const actualWidth = (a / max) * 100;

  const formatNumber = (val) => {
    const n = Math.round(Number(val) || 0);
    if (!currency) return n.toLocaleString('es-CL');
    return `$${n.toLocaleString('es-CL')}`;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="subtitle2">{`${Math.round(pct)}%`}</Typography>
      </Box>
      <Box sx={{ mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
          <Typography variant="caption" sx={{ minWidth: 44 }}>
            Meta
          </Typography>
          <Box sx={{ flex: 1, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${targetWidth}%`,
                height: 8,
                bgcolor: 'grey.500',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ minWidth: 80, textAlign: 'right' }}>
            {formatNumber(t)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ minWidth: 44 }}>
            Real
          </Typography>
          <Box sx={{ flex: 1, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${actualWidth}%`,
                height: 8,
                bgcolor: 'success.main',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ minWidth: 80, textAlign: 'right' }}>
            {formatNumber(a)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const FunnelChart = ({ totalLeads, midLeads, conversionsUnique }) => (
  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
    <Typography variant="subtitle1" gutterBottom fontWeight={700}>
      Embudo de conversión
    </Typography>
    <FunnelStage
      label="1. Solicitudes / Leads"
      value={totalLeads}
      percentOfTop={100}
      color="#3f5c00"
      subtitle="Total de solicitudes ingresadas en el periodo"
    />
    <FunnelStage
      label="2. Leads calificados"
      value={midLeads}
      percentOfTop={totalLeads ? (midLeads / totalLeads) * 100 : 0}
      color="#6b9c00"
      subtitle="Presupuesto enviado, agendado o atendido"
    />
    <FunnelStage
      label="3. Pacientes convertidos"
      value={conversionsUnique}
      percentOfTop={totalLeads ? (conversionsUnique / totalLeads) * 100 : 0}
      color="#a4c400"
      subtitle="Pacientes con presupuesto pagado, cirugía o atención realizada"
    />
  </Box>
);

export default function Dashboard() {
  const { userData } = useAuth();
  const { activeBranchId, activeBranch, filterByBranch, isAdmin, accessibleBranches, setActiveBranchId } = useBranch();

  const isGestionClinica = userData?.role === 'Gestión Clínica';

  useEffect(() => {
    if (isGestionClinica) setTabValue(2);
  }, [isGestionClinica]);

  const currentMonthLabel = getCurrentMonthLabel();

  // Generate month options for selector
  const getMonthOptions = () => {
    const options = [];
    const today = new Date();
    
    // Generate last 12 months and next 3 months
    for (let i = -12; i <= 3; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const year = date.getFullYear();
      const month = date.getMonth();
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      const label = `${months[month]} ${year}`;
      const value = `${year}-${String(month + 1).padStart(2, '0')}`;
      
      options.push({ value, label });
    }
    
    return options.sort((a, b) => b.value.localeCompare(a.value)); // Most recent first
  };

  const [requests, setRequests] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [surgeries, setSurgeries] = useState([]);
  const [goals, setGoals] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('7');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [startDate, setStartDate] = useState(shiftDays(7));
  const [endDate, setEndDate] = useState(getTodayIso());
  const [periodPreset, setPeriodPreset] = useState('7');
  const [tabValue, setTabValue] = useState(0);
  const [goalsPeriod, setGoalsPeriod] = useState(() => {
    // Default to current month
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });

  // Porcentaje de avance del mes (0-100). Base para evaluar el cumplimiento de metas.
  const monthProgressPct = useMemo(() => getMonthProgress(goalsPeriod), [goalsPeriod]);

  const [onlineBookings, setOnlineBookings] = useState([]);
  const [clinicalRecords, setClinicalRecords] = useState([]);
  const [tiposPrestacion, setTiposPrestacion] = useState([]);
  const [professionalSchedules, setProfessionalSchedules] = useState([]);
  const [hospitalizations, setHospitalizations] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [hospitalPatients, setHospitalPatients] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        const [reqs, pres, surgs, gls, sls, bks, recs, tps, pscheds, hos, eg, hp] = await Promise.all([
          getCollection('contact-requests'),
          getCollection('presupuestos'),
          getCollection('surgeries'),
          getCollection('goals'),
          getCollection('Sales'),
          getCollection('onlineBookings'),
          getCollection('clinicalRecords'),
          getCollection('tiposPrestacion'),
          getCollection('professionalSchedules'),
          getCollection('hospitalizations'),
          getCollection('egresosHospitalarios'),
          getCollection('patients'),
        ]);
        setRequests(reqs || []);
        setPresupuestos(pres || []);
        setSurgeries(surgs || []);
        setGoals(gls || []);
        setSales(sls || []);
        setOnlineBookings(bks || []);
        setClinicalRecords(recs || []);
        setTiposPrestacion(tps || []);
        setProfessionalSchedules(pscheds || []);
        setHospitalizations(hos || []);
        setEgresos(eg || []);
        setHospitalPatients(hp || []);
      } catch (err) {
        console.error('Error cargando datos del dashboard:', err);
        setError('Error al cargar datos para el dashboard.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Mapa: meta (name) -> set de nombres de tipoPrestacion que le tributan
  const tiposByGoalMap = useMemo(() => {
    const map = {};
    for (const tp of tiposPrestacion) {
      const goalName = (tp.goalName || tp.meta || '').trim();
      if (!goalName) continue;
      if (!map[goalName]) map[goalName] = new Set();
      map[goalName].add((tp.name || '').trim().toLowerCase());
    }
    return map;
  }, [tiposPrestacion]);

  // Mapa inverso: tipoPrestacion (lowercase) -> meta (name). Relación uno a uno.
  const goalByTipoMap = useMemo(() => {
    const map = {};
    for (const tp of tiposPrestacion) {
      const goalName = (tp.goalName || tp.meta || '').trim();
      const tipoName = (tp.name || '').trim().toLowerCase();
      if (goalName && tipoName) map[tipoName] = goalName;
    }
    return map;
  }, [tiposPrestacion]);

  const branchFilteredRequests = useMemo(() => filterByBranch(requests, { includeUnassigned: true }), [requests, filterByBranch]);
  const branchFilteredPresupuestos = useMemo(() => filterByBranch(presupuestos, { includeUnassigned: true }), [presupuestos, filterByBranch]);
  const branchFilteredSurgeries = useMemo(() => filterByBranch(surgeries, { includeUnassigned: true }), [surgeries, filterByBranch]);
  const branchFilteredSales = useMemo(() => filterByBranch(sales, { includeUnassigned: true }), [sales, filterByBranch]);
  const branchFilteredOnlineBookings = useMemo(() => filterByBranch(onlineBookings, { includeUnassigned: true }), [onlineBookings, filterByBranch]);

  /**
   * Mapa de reservas online para inferir la sucursal de una ficha SOAP.
   * Clave: patientId::vetId/professionalId::fecha (YYYY-MM-DD).
   * Replica la lógica del AgendaDashboardPage.
   */
  const bookingMapForRecords = useMemo(() => {
    const map = {};
    for (const b of onlineBookings) {
      const key = `${b.petId || b.patientId || ''}::${b.professionalId || ''}::${b.date || ''}`;
      if (!map[key]) map[key] = b;
    }
    return map;
  }, [onlineBookings]);

  /**
   * Mapa: professionalId -> Set de branchIds donde tiene horario.
   * Fallback para inferir la sucursal de una ficha SOAP sin branchId ni reserva.
   */
  const professionalBranchMap = useMemo(() => {
    const map = {};
    for (const s of professionalSchedules) {
      const pid = s.professionalId || '';
      const bid = s.branchId || '';
      if (!pid || !bid) continue;
      if (!map[pid]) map[pid] = new Set();
      map[pid].add(bid);
    }
    return map;
  }, [professionalSchedules]);

  /**
   * Filtra clinicalRecords por sucursal, infiriendo en este orden:
   * 1. Reserva asociada (onlineBookings) -> branchId de la reserva.
   * 2. branchId propio del registro.
   * 3. Horario del profesional (professionalSchedules): si el vetId tiene
   *    algun horario en la sucursal activa, se incluye.
   * 4. Si nada se puede inferir -> se excluye.
   */
  const branchFilteredClinicalRecords = useMemo(() => {
    const isAll = activeBranchId === 'all';
    return clinicalRecords.filter((r) => {
      if (isAll) return true;
      const d = getDateFromValue(r.createdAt);
      const dateStr = d ? d.toLocaleDateString('en-CA') : '';
      const bk = bookingMapForRecords[`${r.patientId || ''}::${r.vetId || ''}::${dateStr}`];
      // 1. Si hay reserva, la sucursal se infiere desde ella (filtro estricto)
      if (bk) {
        return (bk.branchId || '') === activeBranchId;
      }
      // 2. Sin reserva: usar branchId del registro
      const branchId = r.branchId || '';
      if (branchId) return branchId === activeBranchId;
      // 3. Sin branchId: inferir desde el horario del profesional (vetId)
      const vetBranches = r.vetId ? professionalBranchMap[r.vetId] : null;
      if (vetBranches && vetBranches.has(activeBranchId)) return true;
      // 4. No se puede inferir -> excluir
      return false;
    });
  }, [clinicalRecords, bookingMapForRecords, professionalBranchMap, activeBranchId]);

  /**
   * Reparto del neto de cada venta entre las metas.
   * El neto de la venta (`subtotalNeto`, igual que Libro de Ventas) es la fuente de verdad
   * y se distribuye proporcionalmente al `subtotal` de sus ítems. Esto garantiza que
   * la suma de todas las metas + lo no asignado sea exactamente el neto de la venta,
   * absorbiendo diferencias de redondeo o ítems que guarden montos brutos.
   */
  // Split de ventas del periodo de metas. Se calcula solo sobre el mes
  // seleccionado, evitando recorrer toda la colección de ventas.
  const salesSplitByGoal = useMemo(() => {
    const result = new Map();
    for (const sale of branchFilteredSales) {
      if ((sale.estado || 'emitida') === 'anulada') continue;
      if (!isInPeriod(sale.createdAt, goalsPeriod)) continue;

      const items = sale.items || [];
      const net = Number(sale.subtotalNeto) || Number(sale.subtotal) || 0;
      const sumItems = items.reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0);
      const factor = sumItems > 0 ? net / sumItems : 0;

      const byGoal = {};
      let unassigned = 0;
      for (const item of items) {
        const value = (Number(item.subtotal) || 0) * factor;
        const goalName = goalByTipoMap[(item.tipoPrestacion || '').trim().toLowerCase()];
        if (goalName) byGoal[goalName] = (byGoal[goalName] || 0) + value;
        else unassigned += value;
      }
      // Si no hay ítems, todo el neto queda sin asignar
      if (sumItems <= 0) unassigned = net;

      result.set(sale.id, {
        byGoal,
        unassigned,
        net,
        branchId: sale.branchId || '',
        presupuestoNumber: sale.presupuestoNumber || '',
        items,
      });
    }
    return result;
  }, [branchFilteredSales, goalByTipoMap, goalsPeriod]);

  // Agregados por meta: cantidad de casos y monto acumulado, por sucursal y total.
  // Calculado una sola vez por periodo, eliminando el filtrado por meta en el render.
  const goalAggregates = useMemo(() => {
    const byBranch = {};
    const all = {};
    let netTotal = 0;
    let unassignedTotal = 0;
    const sinTipo = new Set();
    let salesCount = 0;

    for (const [saleId, split] of salesSplitByGoal.entries()) {
      salesCount += 1;
      netTotal += split.net;
      unassignedTotal += split.unassigned;

      for (const item of split.items || []) {
        const tipo = (item.tipoPrestacion || '').trim();
        if (!tipo) sinTipo.add('(sin tipo de prestación)');
        else if (!goalByTipoMap[tipo.toLowerCase()]) sinTipo.add(tipo);
      }

      const addAgg = (agg) => {
        for (const [goalName, amount] of Object.entries(split.byGoal)) {
          if (amount <= 0) continue;
          if (!agg[goalName]) agg[goalName] = { amount: 0, casos: new Set() };
          agg[goalName].amount += amount;
          const caso = (split.presupuestoNumber || '').trim();
          agg[goalName].casos.add(caso ? `PRES:${caso}` : `VENTA:${saleId}`);
        }
      };

      addAgg(all);
      const bid = split.branchId || 'all';
      if (!byBranch[bid]) byBranch[bid] = {};
      addAgg(byBranch[bid]);
    }

    return {
      all,
      byBranch,
      salesCount,
      netTotal,
      unassignedTotal,
      sinTipo,
    };
  }, [salesSplitByGoal, goalByTipoMap]);

  const handlePresetChange = (e) => {
    const value = e.target.value;
    setPeriodPreset(value);
    if (value === 'custom') return;

    const days = Number(value);
    setStartDate(shiftDays(days));
    setEndDate(getTodayIso());
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setPeriodPreset('custom');
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setPeriodPreset('custom');
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const funnelData = useMemo(() => {
    if (!startDate || !endDate) {
      return {
        totalLeads: 0,
        midLeads: 0,
        conversionsUnique: 0,
        paidPresupuestosCount: 0,
        paidPresupuestosAmount: 0,
        completedSurgeriesCount: 0,
      };
    }

    const from = new Date(`${startDate}T00:00:00`);
    const to = new Date(`${endDate}T23:59:59.999`);

    const inRange = (d) => {
      if (!d) return false;
      return d >= from && d <= to;
    };

    // Etapa 1: todas las solicitudes del periodo (filtradas por sucursal)
    const requestsInRange = branchFilteredRequests.filter((r) => inRange(getDateFromValue(r.createdAt)));
    const totalLeads = requestsInRange.length;

    // Etapa 2: leads que avanzan a Presupuesto Enviado, Agendado o Atendido
    const midStatuses = new Set(['Presupuesto Enviado', 'Agendado', 'Atendido']);
    const midLeads = requestsInRange.filter((r) => midStatuses.has(r.status || ''));
    const midLeadsCount = midLeads.length;

    // Presupuestos pagados en el periodo
    const presupuestosInRange = branchFilteredPresupuestos.filter((p) => inRange(getDateFromValue(p.createdAt)));
    const paidPresupuestos = presupuestosInRange.filter((p) => (p.estado || '').toLowerCase() === 'pagado');
    const paidPresupuestosCount = paidPresupuestos.length;

    // Monto neto total de presupuestos pagados
    const paidPresupuestosAmount = paidPresupuestos.reduce((sum, p) => sum + (Number(p.subtotal) || 0), 0);

    // Cirugías completadas en el periodo
    const surgeriesInRange = branchFilteredSurgeries.filter((s) => inRange(getDateFromValue(s.date || s.createdAt)));
    const completedSurgeries = surgeriesInRange.filter((s) => {
      const status = (s.status || '').toLowerCase();
      return status === 'completada' || status === 'completed';
    });
    const completedSurgeriesCount = completedSurgeries.length;

    // Solicitudes con estado "Atendido" (leads convertidos)
    const attendedRequests = requestsInRange.filter((r) => r.status === 'Atendido');

    // Ventas reales del periodo (no anuladas)
    const salesInRange = branchFilteredSales.filter((s) => inRange(getDateFromValue(s.createdAt)) && (s.estado || '').toLowerCase() !== 'anulada');

    // Conversión: PACIENTES ÚNICOS con al menos un evento real (presupuesto pagado, venta, cirugía o atención)
    const convertedPatients = new Set();
    paidPresupuestos.forEach((p) => {
      if (p.patientId) convertedPatients.add(p.patientId);
    });
    salesInRange.forEach((s) => {
      if (s.patientId) convertedPatients.add(s.patientId);
    });
    completedSurgeries.forEach((s) => {
      if (s.patientId) convertedPatients.add(s.patientId);
    });
    attendedRequests.forEach((r) => {
      if (r.patientId) convertedPatients.add(r.patientId);
    });

    const conversionsUnique = convertedPatients.size;

    const closedRequests = requestsInRange.filter(
      (r) => (r.status || '').toLowerCase() === 'cerrado'
    );
    const closedRequestsCount = closedRequests.length;
    const reasonCounts = {};
    closedRequests.forEach((r) => {
      const raw = (r.closeReason || 'Sin motivo').toString().trim();
      const reason = raw || 'Sin motivo';
      reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
    });

    const closedReasonsStats = Object.entries(reasonCounts).map(([reason, count], index) => {
      const pct = closedRequestsCount ? (count / closedRequestsCount) * 100 : 0;
      return {
        reason,
        count,
        pct,
        color: PIE_COLORS[index % PIE_COLORS.length],
      };
    });

    // Distribución por estado, convenio y origen
    const statusCounts = {};
    requestsInRange.forEach((r) => { const s = r.status || 'Nuevo'; statusCounts[s] = (statusCounts[s] || 0) + 1; });
    const convenioCounts = {};
    requestsInRange.forEach((r) => { const c = r.convenioName || r.convenio || 'Sin convenio'; convenioCounts[c] = (convenioCounts[c] || 0) + 1; });
    const sourceCounts = {};
    requestsInRange.forEach((r) => { const s = r.source || 'Manual'; sourceCounts[s] = (sourceCounts[s] || 0) + 1; });

    // Tiempo promedio de conversión (días entre creación y updatedAt para leads Atendido)
    const attendedForTime = requestsInRange.filter((r) => r.status === 'Atendido' && r.updatedAt && r.createdAt);
    const totalDays = attendedForTime.reduce((sum, r) => {
      const d1 = getDateFromValue(r.createdAt);
      const d2 = getDateFromValue(r.updatedAt);
      if (d1 && d2) return sum + Math.max(0, (d2 - d1) / (1000 * 60 * 60 * 24));
      return sum;
    }, 0);
    const avgConversionDays = attendedForTime.length ? totalDays / attendedForTime.length : 0;

    return {
      totalLeads,
      midLeads: midLeadsCount,
      conversionsUnique,
      paidPresupuestosCount,
      paidPresupuestosAmount,
      completedSurgeriesCount,
      closedRequestsCount,
      closedReasonsStats,
      statusCounts,
      convenioCounts,
      sourceCounts,
      avgConversionDays,
    };
  }, [startDate, endDate, branchFilteredRequests, branchFilteredPresupuestos, branchFilteredSales, branchFilteredSurgeries]);

  const {
    totalLeads,
    midLeads,
    conversionsUnique,
    paidPresupuestosCount,
    paidPresupuestosAmount,
    completedSurgeriesCount,
    closedRequestsCount,
    closedReasonsStats,
    statusCounts,
    convenioCounts,
    sourceCounts,
    avgConversionDays,
  } = funnelData;

  const midRate = totalLeads ? (midLeads / totalLeads) * 100 : 0;
  const conversionRate = totalLeads ? (conversionsUnique / totalLeads) * 100 : 0;

  const monthlyGoals = useMemo(
    () => {
      return goals.filter((g) => {
        const isMonthly = (g.type || 'monthly') === 'monthly';
        const isActive = g.isActive !== false;
        const periodMatches = !g.period || g.period === goalsPeriod;
        const branchMatches = activeBranchId === 'all' || !g.branchId || g.branchId === activeBranchId;

        return isMonthly && isActive && periodMatches && branchMatches;
      });
    },
    [goals, goalsPeriod, activeBranchId]
  );

  /**
   * Valor real de una meta: lee los agregados precalculados para el periodo,
   * evitando recorrer las ventas en cada render.
   */
  const computeGoalReal = (goal) => {
    const goalName = (goal.name || '').trim();
    const mappedTipoNames = tiposByGoalMap[goalName];
    if (!goalName || !mappedTipoNames || mappedTipoNames.size === 0) {
      return { quantity: 0, amount: 0, unlinked: true };
    }

    const agg = goal.branchId
      ? goalAggregates.byBranch[goal.branchId]?.[goalName]
      : goalAggregates.all[goalName];

    return {
      quantity: agg ? agg.casos.size : 0,
      amount: agg ? agg.amount : 0,
      unlinked: false,
    };
  };

  const getGoalStatus = (targetQty, targetAmt, quantity, amount) => {
    const tQty = Number(targetQty) || 0;
    const tAmt = Number(targetAmt) || 0;
    const realQty = Number(quantity) || 0;
    const realAmt = Number(amount) || 0;

    // Preferir evaluación por monto si hay meta de monto; si no, por cantidad
    let target = tAmt;
    let real = realAmt;
    if (tAmt === 0 && tQty > 0) {
      target = tQty;
      real = realQty;
    }

    if (!target) {
      return { label: 'Sin meta', color: 'default', pct: 0, trend: 'Sin datos' };
    }

    const pct = (real / target) * 100;
    const threshold = monthProgressPct;

    // Meta cumplida o superada
    if (pct >= 100) {
      return { label: `${pct.toFixed(0)}% · Cumplida`, color: 'success', pct, trend: 'On track' };
    }

    // Verde: cumplimiento >= avance del mes
    if (pct >= threshold) {
      return { label: `${pct.toFixed(0)}% · On track`, color: 'success', pct, trend: 'On track' };
    }

    // Amarillo: hasta 3 puntos porcentuales bajo el avance del mes
    if (pct >= threshold - 3) {
      return { label: `${pct.toFixed(0)}% · Requiere atención`, color: 'warning', pct, trend: 'Requiere atención' };
    }

    // Rojo: más de 3 puntos bajo el avance del mes
    // Riesgo alto si está 10+ puntos porcentuales bajo el avance
    if (pct < threshold - 10) {
      return { label: `${pct.toFixed(0)}% · En riesgo alto`, color: 'error', pct, trend: 'En riesgo alto' };
    }
    return { label: `${pct.toFixed(0)}% · En riesgo medio`, color: 'error', pct, trend: 'En riesgo medio' };
  };

  const getGoalVisualConfig = (goal) => {
    const name = (goal.name || '').trim().toLowerCase();
    const tQty = Number(goal.targetQuantity) || 0;
    const tAmt = Number(goal.targetAmount) || 0;

    let icon = <TrendingUp fontSize="small" />;
    let bgColor = 'primary.light';
    let fgColor = 'primary.main';
    let categoryLabel = 'Actividad';

    if (name.includes('cirug')) {
      icon = <LocalHospital fontSize="small" />;
      bgColor = 'error.light';
      fgColor = 'error.main';
      categoryLabel = 'Cirugías';
    } else if (name.includes('consulta') || name.includes('atención')) {
      icon = <AssignmentTurnedIn fontSize="small" />;
      bgColor = 'info.light';
      fgColor = 'info.main';
      categoryLabel = 'Consultas';
    } else if (tAmt > 0 && tQty === 0) {
      icon = <MonetizationOn fontSize="small" />;
      bgColor = 'success.light';
      fgColor = 'success.main';
      categoryLabel = 'Ventas';
    } else if (tQty > 0 && tAmt === 0) {
      icon = <Group fontSize="small" />;
      bgColor = 'warning.light';
      fgColor = 'warning.main';
      categoryLabel = 'Volumen';
    }

    return { icon, bgColor, fgColor, categoryLabel };
  };

  const getTrendLabel = (pct) => {
    if (!Number.isFinite(pct) || pct <= 0) return 'Sin datos';
    if (pct >= 100) return 'On track';
    if (pct >= monthProgressPct) return 'On track';
    if (pct >= monthProgressPct - 3) return 'Requiere atención';
    if (pct < monthProgressPct - 10) return 'En riesgo alto';
    return 'En riesgo medio';
  };

  /**
   * Atenciones realizadas en el periodo = número de fichas SOAP (clinicalRecords)
   * creadas en el mes. Es el denominador de la tasa de derivación.
   */
  const attentionsInPeriod = useMemo(
    () => branchFilteredClinicalRecords.filter((r) => isInPeriod(r.createdAt, goalsPeriod)).length,
    [branchFilteredClinicalRecords, goalsPeriod]
  );

  const sortedMonthlyGoals = useMemo(
    () =>
      monthlyGoals
        .map((goal) => {
          const { quantity, amount, unlinked } = computeGoalReal(goal);
          const targetQty = goal.targetQuantity || 0;
          const targetAmt = goal.targetAmount || 0;
          const status = getGoalStatus(targetQty, targetAmt, quantity, amount);
          // Tasa de derivación: casos derivados a la meta / atenciones realizadas (fichas SOAP)
          const referralRate = attentionsInPeriod > 0 ? (quantity / attentionsInPeriod) * 100 : null;
          // Tasa de derivación esperada según la meta de cantidad
          const targetReferralRate = attentionsInPeriod > 0 && targetQty > 0
            ? (targetQty / attentionsInPeriod) * 100
            : null;
          return { goal, quantity, amount, unlinked, status, referralRate, targetReferralRate };
        })
        .sort((a, b) => (b.status.pct || 0) - (a.status.pct || 0)),
    [monthlyGoals, tiposByGoalMap, goalAggregates, attentionsInPeriod]
  );

  const goalsSalesSummary = useMemo(
    () => {
      const targetAmount = monthlyGoals.reduce((sum, g) => sum + (Number(g.targetAmount) || 0), 0);
      const targetQty = monthlyGoals.reduce((sum, g) => sum + (Number(g.targetQuantity) || 0), 0);
      const actualAmount = sortedMonthlyGoals.reduce((sum, g) => sum + g.amount, 0);
      const actualQty = sortedMonthlyGoals.reduce((sum, g) => sum + g.quantity, 0);
      const pct = targetAmount ? (actualAmount / targetAmount) * 100 : 0;
      return { targetAmount, targetQty, actualAmount, actualQty, pct };
    },
    [monthlyGoals, sortedMonthlyGoals]
  );

  const {
    targetAmount: totalSalesTarget,
    targetQty: totalGoalsQty,
    actualAmount: totalSalesReal,
    actualQty: totalRealQty,
    pct: totalSalesPct,
  } = goalsSalesSummary;

  // Control de cuadratura: neto del periodo (Libro de Ventas) vs monto asignado a metas
  const periodSalesCheck = useMemo(() => {
    const { salesCount, netTotal, unassignedTotal, sinTipo } = goalAggregates;
    return {
      salesCount,
      netTotal,
      asignadoAMetas: netTotal - unassignedTotal,
      sinAsignar: unassignedTotal,
      tiposSinMeta: Array.from(sinTipo).sort((a, b) => a.localeCompare(b, 'es')),
    };
  }, [goalAggregates]);

  return (
    <Box>
      <Box className="dashboard-header">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 1 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Dashboard
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              Hola, {userData?.email}. Este embudo muestra la conversión de solicitudes de atención a cirugías/budgets realizados en el periodo seleccionado.
            </Typography>
          </Box>
          {accessibleBranches.length > 0 && (
            <TextField
              select
              size="small"
              label="Sucursal"
              value={activeBranchId}
              onChange={(e) => setActiveBranchId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              {isAdmin && (
                <MenuItem value="all">Todas las sucursales</MenuItem>
              )}
              {accessibleBranches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            {!isGestionClinica && <Tab value={0} label="Embudo de Conversión" />}
            {!isGestionClinica && <Tab value={1} label="Metas Mensuales" />}
            <Tab value={2} label="Casuística Hospital" />
            {!isGestionClinica && <Tab value={3} label="Cirugías" />}
            {isGestionClinica && <Tab value={3} label="Indicadores de gestión quirúrgica" />}
            {isGestionClinica && <Tab value={4} label="Hospital - Egresos" />}
            {isGestionClinica && <Tab value={5} label="Agenda" />}
          </Tabs>
        </Box>
      </Box>
      {tabValue === 0 && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 3
                }}>
                <TextField
                  select
                  label="Periodo"
                  fullWidth
                  size="small"
                  value={periodPreset}
                  onChange={handlePresetChange}
                >
                  {PERIOD_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 3
                }}>
                <TextField
                  label="Desde"
                  type="date"
                  fullWidth
                  size="small"
                  value={startDate}
                  onChange={handleStartDateChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 3
                }}>
                <TextField
                  label="Hasta"
                  type="date"
                  fullWidth
                  size="small"
                  value={endDate}
                  onChange={handleEndDateChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Embudo de Conversión de Leads
            </Typography>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!loading && !error && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    mb: 2,
                    overflowX: 'auto',
                    pb: 1,
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      minWidth: 240,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                    variant="outlined"
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Group fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Total solicitudes en el periodo
                      </Typography>
                      <Typography variant="h5">{totalLeads}</Typography>
                    </Box>
                  </Paper>
                  <Paper
                    sx={{
                      p: 2,
                      minWidth: 240,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                    variant="outlined"
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        bgcolor: 'secondary.main',
                        color: 'secondary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AssignmentTurnedIn fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Leads con presupuesto enviado o agendado
                      </Typography>
                      <Typography variant="h5">{midLeads}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {totalLeads ? midRate.toFixed(1) : '0.0'}% de las solicitudes
                      </Typography>
                    </Box>
                  </Paper>
                  <Paper
                    sx={{
                      p: 2,
                      minWidth: 240,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                    variant="outlined"
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        color: 'success.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <TrendingUp fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Pacientes convertidos
                      </Typography>
                      <Typography variant="h5">{conversionsUnique}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {totalLeads ? conversionRate.toFixed(1) : '0.0'}% de las solicitudes
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                        (Con presupuesto pagado, cirugía o atención)
                      </Typography>
                    </Box>
                  </Paper>
                </Box>

                <FunnelChart
                  totalLeads={totalLeads}
                  midLeads={midLeads}
                  conversionsUnique={conversionsUnique}
                />

                <Grid container spacing={2} sx={{ mt: 3 }}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Paper sx={{ p: 2 }} variant="outlined">
                      <Typography variant="subtitle2" color="text.secondary">
                        Presupuestos pagados
                      </Typography>
                      <Typography variant="h5">{paidPresupuestosCount}</Typography>
                      <Typography variant="body2" color="success.main" sx={{ mt: 1, fontWeight: 600 }}>
                        Monto neto: ${Math.round(paidPresupuestosAmount).toLocaleString('es-CL')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Paper sx={{ p: 2 }} variant="outlined">
                      <Typography variant="subtitle2" color="text.secondary">
                        Cirugías completadas
                      </Typography>
                      <Typography variant="h5">{completedSurgeriesCount}</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: 3 }}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 3
                    }}>
                    <Paper sx={{ p: 2 }} variant="outlined">
                      <Typography variant="subtitle2" color="text.secondary">Tiempo promedio de conversión</Typography>
                      <Typography variant="h5">{avgConversionDays.toFixed(1)} días</Typography>
                    </Paper>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 3
                    }}>
                    <Paper sx={{ p: 2 }} variant="outlined">
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Leads por estado</Typography>
                      {Object.entries(statusCounts).map(([s, c]) => (
                        <Box key={s} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption">{s}</Typography>
                          <Typography variant="caption" fontWeight={600}>{c}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 3
                    }}>
                    <Paper sx={{ p: 2 }} variant="outlined">
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Leads por convenio</Typography>
                      {Object.entries(convenioCounts).map(([s, c]) => (
                        <Box key={s} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption">{s}</Typography>
                          <Typography variant="caption" fontWeight={600}>{c}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 3
                    }}>
                    <Paper sx={{ p: 2 }} variant="outlined">
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Leads por origen</Typography>
                      {Object.entries(sourceCounts).map(([s, c]) => (
                        <Box key={s} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption">{s}</Typography>
                          <Typography variant="caption" fontWeight={600}>{c}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Typography variant="h6" gutterBottom>
                      Motivos de cierre de solicitudes
                    </Typography>
                    {closedRequestsCount === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No hay solicitudes cerradas en el periodo seleccionado.
                      </Typography>
                    ) : (
                      <ClosedReasonsPie data={closedReasonsStats} total={closedRequestsCount} />
                    )}
                  </Paper>
                </Box>
              </>
            )}
          </Paper>
        </>
      )}
      {tabValue === 1 && (
        <Box sx={{ mt: 1 }}>
          <Paper sx={{ p: 2 }} className="monthly-goals-report">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6">
                  Informe de ventas Hospital Clínico Veterinario UDLA Melipilla
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Periodo:
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={goalsPeriod}
                    onChange={(e) => setGoalsPeriod(e.target.value)}
                    sx={{ minWidth: 180 }}
                  >
                    {getMonthOptions().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<PictureAsPdf />}
                onClick={() => window.print()}
              >
                Descargar PDF
              </Button>
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Metas Mensuales
            </Typography>

            {monthlyGoals.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No hay metas mensuales configuradas. Puedes crearlas en Configuración → Metas.
              </Typography>
            )}

            {monthlyGoals.length > 0 && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'success.light',
                        color: 'success.dark',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <MonetizationOn fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">Resumen ventas totales (neto)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Suma de subtotales netos de Sales agrupados por tipo de prestación
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 3,
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="subtitle2"
                        color="text.primary"
                        sx={{ fontWeight: 600 }}
                      >
                        Meta
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 700, color: 'text.primary' }}
                      >
                        {`$${Math.round(totalSalesTarget || 0).toLocaleString('es-CL')}`}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="subtitle2"
                        color="text.primary"
                        sx={{ fontWeight: 600 }}
                      >
                        Real
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 700, color: 'text.primary' }}
                      >
                        {`$${Math.round(totalSalesReal || 0).toLocaleString('es-CL')}`}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${Math.round(totalSalesPct || 0)}%`}
                      color={
                        totalSalesPct < 70
                          ? 'error'
                          : totalSalesPct < 100
                          ? 'warning'
                          : 'success'
                      }
                    />
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <GoalBar
                    title="Ventas Totales $"
                    target={totalSalesTarget}
                    actual={totalSalesReal}
                    currency
                  />
                  <GoalBar
                    title="Ticket promedio global $"
                    target={totalGoalsQty > 0 ? totalSalesTarget / totalGoalsQty : 0}
                    actual={totalRealQty > 0 ? totalSalesReal / totalRealQty : 0}
                    currency
                  />
                </Box>

                <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Cuadratura del periodo
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Ventas netas del periodo: ${Math.round(periodSalesCheck.netTotal).toLocaleString('es-CL')} ({periodSalesCheck.salesCount} pagos) ·
                    Asignado a metas: ${Math.round(periodSalesCheck.asignadoAMetas).toLocaleString('es-CL')} ·
                    Sin asignar: ${Math.round(periodSalesCheck.sinAsignar).toLocaleString('es-CL')}
                  </Typography>
                  {periodSalesCheck.tiposSinMeta.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Tipos de prestación sin meta asociada (no suman a ninguna meta):{' '}
                      {periodSalesCheck.tiposSinMeta.join(' · ')}. Vincúlalos en Aranceles → Tipos de Prestación.
                    </Alert>
                  )}
                </Paper>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="body2">
                          <strong>Atenciones realizadas (fichas SOAP) en el periodo:</strong>{' '}
                          {attentionsInPeriod.toLocaleString('es-CL')}
                        </Typography>
                        <Chip
                          size="small"
                          label={`Avance del mes: ${monthProgressPct.toFixed(0)}%`}
                          color={monthProgressPct >= 100 ? 'success' : 'primary'}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Base usada para el cálculo de la tasa de derivación a cada meta. El cumplimiento se evalúa según el avance del mes.
                      </Typography>
                    </Paper>
                  </Grid>
                  {sortedMonthlyGoals.map(({ goal, quantity, amount, unlinked, status, referralRate, targetReferralRate }) => {
                    const targetQty = goal.targetQuantity || 0;
                    const targetAmt = goal.targetAmount || 0;
                    const visual = getGoalVisualConfig(goal);

                    return (
                      <Grid
                        key={goal.id}
                        size={{
                          xs: 12,
                          md: 6
                        }}>
                        <Paper sx={{ p: 2 }} variant="outlined">
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 0.5,
                              pb: 0.5,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  bgcolor: visual.bgColor,
                                  color: visual.fgColor,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {visual.icon}
                              </Box>
                              <Box>
                                <Typography variant="subtitle1">{goal.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {visual.categoryLabel}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              size="small"
                              label={status.label}
                              color={status.color}
                              variant={status.color === 'default' ? 'outlined' : 'filled'}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {goal.period || 'Meta mensual'} · {goal.branchName || (goal.branchId ? '—' : 'Todas las sucursales')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            Tendencia: {status.trend || getTrendLabel(status.pct)} · Avance del mes: {monthProgressPct.toFixed(0)}%
                          </Typography>

                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Cantidad: {quantity.toLocaleString('es-CL')} / {Number(targetQty || 0).toLocaleString('es-CL')} · Ventas netas: $
                            {Math.round(amount).toLocaleString('es-CL')} / ${Math.round(Number(targetAmt) || 0).toLocaleString('es-CL')}
                          </Typography>

                          <Box
                            sx={{
                              mt: 1,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: 'background.paper',
                              border: '1px dashed',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Tasa de derivación
                            </Typography>
                            <Typography variant="h6" component="div">
                              {referralRate === null
                                ? 'N/D'
                                : `${referralRate.toFixed(1)}%`}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                {targetReferralRate === null
                                  ? ''
                                  : `(meta ${targetReferralRate.toFixed(1)}%)`}
                              </Typography>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {quantity.toLocaleString('es-CL')} caso(s) derivados /{' '}
                              {attentionsInPeriod.toLocaleString('es-CL')} atención(es) SOAP
                            </Typography>
                          </Box>

                          {unlinked && (
                            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                              Sin tipos de prestación vinculados a esta meta.
                            </Alert>
                          )}

                          <Box sx={{ mt: 2 }}>
                            <GoalBar
                              title="Cantidad (casos)"
                              target={targetQty}
                              actual={quantity}
                              currency={false}
                            />
                            <GoalBar title="Ventas $" target={targetAmt} actual={amount} currency />
                            <GoalBar
                              title="Ticket promedio $"
                              target={targetQty > 0 ? targetAmt / targetQty : 0}
                              actual={quantity > 0 ? amount / quantity : 0}
                              currency
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </Paper>
        </Box>
      )}
      {tabValue === 2 && (
        <Box sx={{ mt: 2 }}>
          <HospitalCasuistryTab />
        </Box>
      )}
      {tabValue === 3 && (
        <Box sx={{ mt: 2 }}>
          <SurgeryDashboardTab />
        </Box>
      )}
      {tabValue === 4 && (
        <Box sx={{ mt: 2 }}>
          <HospitalEgresosStatsTab
            egresos={egresos}
            patients={hospitalPatients}
            hospitalizations={hospitalizations}
          />
        </Box>
      )}
      {tabValue === 5 && (
        <Box sx={{ mt: 2 }}>
          <AgendaDashboardPage onlyAgenda />
        </Box>
      )}
    </Box>
  );
}
